import { z } from 'zod';

import { answerInputs, answerProblems } from '@mds/shared/lib/form-answers';
import type { FormQuestionSet } from '@mds/shared/types/form-question';
import { email, phone, required, tooLong } from '@mds/shared/lib/validation';

/**
 * Mirrors `createGuestEventRegistrationSchema` in the API's guest-events
 * module, less `requestId` and `formVersion` (added at submit) and
 * `seatCount`: one registration is one seat at MDS, which is the API's default.
 *
 * Built per event, because the questions are the event's own. They are checked
 * and converted by `lib/form-answers` — the same rules as the feedback form and
 * the admin.
 */
export const registrationSchema = (questions: FormQuestionSet) =>
  z
    .object({
      attendeeName: required('Full name').max(160, tooLong(160)),
      attendeeEmail: email,
      // Required: MDS calls a guest who is waitlisted or has not paid yet.
      attendeePhone: phone,
      /** Opts this address into MDS announcements. Off unless ticked. */
      joinMailingList: z.boolean(),
      /** Keyed by question id, which is what `FormQuestions` binds each field to. */
      answers: z.record(z.string(), z.union([z.string(), z.array(z.string())])),
    })
    .superRefine((values, ctx) => {
      // Each problem lands under its own question, where `FormQuestions` shows it.
      for (const [questionId, message] of Object.entries(answerProblems(questions, values.answers))) {
        ctx.addIssue({ code: 'custom', path: ['answers', questionId], message });
      }
    })
    .transform(({ answers, ...values }) => ({
      ...values,
      answers: answerInputs(questions, answers),
    }));
